import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Crown,
  Star,
  Check,
  ArrowRight,
  MessageCircle,
  Video,
  Bot,
  Zap,
  Users,
  Clock,
  Shield,
  Sparkles,
  Loader2,
  ArrowLeft,
  DollarSign
} from "lucide-react";
import { MetaPixel } from "../utils/metaPixel";

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_DETAILS = {
  premium: {
    name: "Premium",
    tagline: "Acompañamiento Humano + IA",
    price: 49.99,
    color: "emerald",
    icon: Star,
    gradient: "from-emerald-500 to-teal-600",
    features: [
      { icon: MessageCircle, text: "Chat con Asesor Experto", desc: "Respuesta en 2-3 días" },
      { icon: Bot, text: "Simulador de Entrevista IA", desc: "Practica ilimitado" },
      { icon: Zap, text: "Optimización de Cita", desc: "Mejora tu probabilidad" },
      { icon: Shield, text: "Revisión de Documentos", desc: "Por expertos humanos" },
    ],
    popular: false,
  },
  vip: {
    name: "VIP Enterprise",
    tagline: "Adelanto de cita consular incluido",
    price: 99.99,
    color: "amber",
    icon: Crown,
    gradient: "from-amber-500 to-orange-600",
    scarcity: "Solo quedan 3 cupos — Oferta limitada",
    features: [
      { icon: Zap, text: "Adelanto de Cita Consular INCLUIDA", desc: "Promedio 300 días calendario de adelanto" },
      { icon: Users, text: "BONUS: 3 días Orlando + 3 días Miami", desc: "PAQUETE PARA UNA PAREJA" },
      { icon: DollarSign, text: "BONO ÚNICO: $500 USD para hoteles", desc: "+3 millones de propiedades — Solo quedan 3 cupos" },
      { icon: MessageCircle, text: "Chat con Asesor Experto", desc: "Respuesta prioritaria" },
      { icon: Bot, text: "Simulador de Entrevista IA", desc: "Practica ilimitado" },
      { icon: Video, text: "Videollamada Zoom Directa", desc: "Agenda cuando quieras" },
      { icon: Users, text: "Oficial Dedicado", desc: "Tu experto personal" },
      { icon: Clock, text: "Atención Lun-Sáb 9am-6pm", desc: "Respuesta < 24 horas" },
    ],
    popular: true,
  }
};

const PlanCard = ({ planKey, plan, currentPlan, onSelect, isLoading, selectedPlan }) => {
  const Icon = plan.icon;
  const isCurrentPlan = currentPlan === planKey;
  const isSelected = selectedPlan === planKey;
  const canUpgrade = !isCurrentPlan && (
    (currentPlan === "estandar" || currentPlan === "standard" || currentPlan === "prueba") ||
    (currentPlan === "premium" && planKey === "vip")
  );
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={canUpgrade ? { scale: 1.02 } : {}}
      className={`relative rounded-2xl overflow-hidden transition-all ${
        isSelected ? "ring-4 ring-offset-2" : ""
      } ${
        planKey === "vip" 
          ? "ring-amber-400" 
          : "ring-emerald-400"
      }`}
    >
      {/* Popular Badge */}
      {plan.popular && (
        <div className="absolute top-0 right-0 z-10">
          <div className={`bg-gradient-to-r ${plan.gradient} text-white text-xs font-bold px-4 py-1 rounded-bl-xl`}>
            MÁS POPULAR
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className={`bg-gradient-to-r ${plan.gradient} p-6 text-white`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-bold text-xl">{plan.name}</h3>
            <p className="text-white/80 text-sm">{plan.tagline}</p>
          </div>
        </div>
        
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold">${plan.price}</span>
          <span className="text-white/70">USD</span>
        </div>
      </div>
      
      {/* Features */}
      <div className="bg-white p-6">
        <ul className="space-y-3">
          {plan.features.map((feature, idx) => {
            const FeatureIcon = feature.icon;
            return (
              <li key={idx} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  planKey === "vip" ? "bg-amber-100" : "bg-emerald-100"
                }`}>
                  <FeatureIcon className={`w-4 h-4 ${
                    planKey === "vip" ? "text-amber-600" : "text-emerald-600"
                  }`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-navy-800">{feature.text}</p>
                  <p className="text-xs text-navy-500">{feature.desc}</p>
                </div>
              </li>
            );
          })}
        </ul>
        
        {/* CTA */}
        <div className="mt-6">
          {isCurrentPlan ? (
            <div className="w-full py-3 px-4 bg-gray-100 text-gray-500 rounded-xl text-center font-medium">
              Tu plan actual
            </div>
          ) : canUpgrade ? (
            <Button
              onClick={() => onSelect(planKey)}
              disabled={isLoading}
              className={`w-full py-6 text-lg font-bold bg-gradient-to-r ${plan.gradient} hover:opacity-90 transition-opacity`}
              data-testid={`upgrade-to-${planKey}-btn`}
            >
              {isLoading && isSelected ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <Sparkles className="w-5 h-5 mr-2" />
              )}
              {isLoading && isSelected ? "Procesando..." : `Mejorar a ${plan.name}`}
            </Button>
          ) : (
            <div className="w-full py-3 px-4 bg-gray-50 text-gray-400 rounded-xl text-center text-sm">
              Ya tienes un plan superior
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const UpgradePlanPage = () => {
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralName, setReferralName] = useState("");
  const [checkingCode, setCheckingCode] = useState(false);
  
  // Pre-select plan from URL if provided
  const preselectedPlan = searchParams.get("plan");
  const urlReferralCode = searchParams.get("ref");
  
  useEffect(() => {
    // Track page view
    MetaPixel.trackViewContent({
      content_name: "Upgrade Plan Page",
      content_category: "Plan Upgrade"
    });
    
    // Pre-fill referral code from URL
    if (urlReferralCode) {
      setReferralCode(urlReferralCode);
      validateReferralCode(urlReferralCode);
    }
  }, []);
  
  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      setReferralName("");
      return;
    }
    
    setCheckingCode(true);
    try {
      const response = await fetch(`${API}/api/referrals/validate/${code}`);
      const data = await response.json();
      
      if (response.ok && data.valid) {
        setReferralValid(true);
        setReferralName(data.referrer_name);
      } else {
        setReferralValid(false);
        setReferralName("");
      }
    } catch (error) {
      setReferralValid(false);
      setReferralName("");
    } finally {
      setCheckingCode(false);
    }
  };
  
  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setReferralCode(code);
    
    // Debounce validation
    if (code.length >= 4) {
      validateReferralCode(code);
    } else {
      setReferralValid(null);
      setReferralName("");
    }
  };
  
  const currentPlan = user?.plan_type?.toLowerCase() || "estandar";
  const normalizedPlan = currentPlan === "advanced" ? "premium" : currentPlan;
  
  // Determine which plans to show based on current plan
  const availablePlans = [];
  if (normalizedPlan === "estandar" || normalizedPlan === "standard" || normalizedPlan === "prueba") {
    availablePlans.push("premium", "vip");
  } else if (normalizedPlan === "premium") {
    availablePlans.push("vip");
  }
  
  const handleSelectPlan = async (planKey) => {
    setSelectedPlan(planKey);
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      
      // ====== CHECK FOR DUPLICATE CASE FIRST ======
      const duplicateCheck = await fetch(`${API}/api/payments/check-duplicate/plan_${planKey}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const duplicateData = await duplicateCheck.json();
      
      if (duplicateData.has_duplicate) {
        toast.error(
          `Ya tienes un caso activo con el plan ${duplicateData.existing_case?.plan_name || planKey}. ` +
          `Si necesitas ayuda adicional, contacta a soporte.`,
          { duration: 6000 }
        );
        setIsLoading(false);
        setSelectedPlan(null);
        return;
      }
      // ====== END DUPLICATE CHECK ======
      
      // Track initiate checkout
      MetaPixel.trackInitiateCheckout({
        plan_name: PLAN_DETAILS[planKey].name,
        plan_id: `plan_${planKey}`,
        price: PLAN_DETAILS[planKey].price
      });
      
      const payload = { target_plan: planKey };
      
      // Include referral code if valid
      if (referralCode && referralValid) {
        payload.referral_code = referralCode;
      }
      
      const response = await fetch(`${API}/api/payments/upgrade`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || "Error al procesar el pago");
      }
      
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No se recibió URL de pago");
      }
    } catch (error) {
      console.error("Error upgrading:", error);
      toast.error(error.message || "Error al procesar el upgrade");
      setIsLoading(false);
      setSelectedPlan(null);
    }
  };
  
  // If VIP, show message that they have the best plan
  if (normalizedPlan === "vip") {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Crown className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-navy-900 mb-3">
            ¡Ya tienes el mejor plan!
          </h1>
          <p className="text-navy-600 mb-8">
            Con VIP Enterprise tienes acceso a todas las funciones disponibles.
          </p>
          <Button
            onClick={() => navigate("/dashboard")}
            className="bg-navy-700 hover:bg-navy-800"
            data-testid="go-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Dashboard
          </Button>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-navy-900 mb-3">
              Mejora tu plan
            </h1>
            <p className="text-navy-600 max-w-xl mx-auto">
              Desbloquea funciones premium y obtén acompañamiento personalizado 
              para maximizar tus posibilidades de éxito.
            </p>
          </motion.div>
          
          {/* Current plan indicator */}
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-navy-100 rounded-full">
            <Shield className="w-4 h-4 text-navy-500" />
            <span className="text-sm text-navy-600">
              Tu plan actual: <strong className="text-navy-800">{normalizedPlan === "estandar" ? "Estándar" : normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1)}</strong>
            </span>
          </div>
        </div>
        
        {/* Plans Grid */}
        <div className={`grid gap-6 ${availablePlans.length > 1 ? "md:grid-cols-2" : "max-w-md mx-auto"}`}>
          {availablePlans.map((planKey) => (
            <PlanCard
              key={planKey}
              planKey={planKey}
              plan={PLAN_DETAILS[planKey]}
              currentPlan={normalizedPlan}
              onSelect={handleSelectPlan}
              isLoading={isLoading}
              selectedPlan={selectedPlan}
            />
          ))}
        </div>
        
        {/* Referral Code Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 max-w-md mx-auto"
        >
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ¿Tienes un código de referido?
            </label>
            <div className="relative">
              <input
                type="text"
                value={referralCode}
                onChange={handleReferralCodeChange}
                placeholder="Ingresa el código (opcional)"
                maxLength={12}
                className={`w-full px-4 py-2.5 border rounded-lg text-sm uppercase tracking-wider font-mono transition-colors ${
                  referralValid === true 
                    ? "border-emerald-400 bg-emerald-50 focus:ring-emerald-500" 
                    : referralValid === false 
                    ? "border-red-400 bg-red-50" 
                    : "border-gray-200 focus:ring-blue-500"
                } focus:outline-none focus:ring-2`}
              />
              {checkingCode && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              )}
              {referralValid === true && !checkingCode && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Check className="w-4 h-4 text-emerald-500" />
                </div>
              )}
            </div>
            {referralValid === true && referralName && (
              <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Referido por: <strong>{referralName}</strong>
              </p>
            )}
            {referralValid === false && referralCode.length >= 4 && (
              <p className="text-xs text-red-500 mt-1.5">
                Código no válido
              </p>
            )}
          </div>
        </motion.div>
        
        {/* Guarantee Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-10 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100"
        >
          <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-7 h-7 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-navy-900 mb-1">Garantía de Satisfacción 7 días</h3>
              <p className="text-sm text-navy-600">
                Si no cumplimos tus expectativas dentro de los primeros 7 días, 
                te devolvemos el 100% de tu dinero. Sin preguntas.
              </p>
            </div>
          </div>
        </motion.div>
        
        {/* Back button */}
        <div className="text-center mt-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-navy-500 hover:text-navy-700"
            data-testid="go-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UpgradePlanPage;
